import { useEffect, useRef } from "react";
import { C } from "./theme";
import { PAGE_WIDTH } from "./layout";

// The full-screen add/edit dialog used by Dreams, Fears and Vision: a title, Save and ✕ at the top,
// and the form scrolling underneath. Escape closes it, except while `busy` (closing then would hide
// whether the save worked).
const FOCUSABLE = "button:not([disabled]), textarea, input, select, [href], [tabindex]:not([tabindex='-1'])";

export function EditorFrame({ id, title, canSave, saving, busy, onSave, onClose, children }) {
  const frame = useRef(null);
  const heading = useRef(null);

  // Focus moves into the dialog when it opens (to the heading, unless a field took it), and goes back to
  // whatever opened it when it closes.
  useEffect(() => {
    const opener = document.activeElement;
    if (!frame.current.contains(document.activeElement)) heading.current.focus();
    return () => { if (opener && document.contains(opener)) opener.focus(); };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
      // Tab stays inside the dialog: the page behind it is covered.
      if (e.key !== "Tab") return;
      const items = [...frame.current.querySelectorAll(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !frame.current.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  return (
    <div ref={frame} role="dialog" aria-modal="true" aria-labelledby={id}
      style={{ position: "absolute", inset: 0, zIndex: 110, background: C.bg, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "12px 16px 12px 20px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderBottom: `1px solid ${C.border}` }}>
        <h2 id={id} ref={heading} tabIndex={-1} style={{ outline: "none", margin: 0, fontWeight: 700, fontSize: 19, color: C.text }}>{title}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={onSave} disabled={!canSave}
            style={{ minHeight: 44, background: canSave ? C.accent : C.surface2, color: canSave ? C.onAccent : C.textDim, border: "none", borderRadius: C.rsm, padding: "0 18px", fontSize: 15, fontWeight: 700, cursor: canSave ? "pointer" : "default", fontFamily: "inherit" }}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} aria-label="Close without saving"
            style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: C.rsm, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.textMid }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ maxWidth: PAGE_WIDTH, margin: "0 auto", padding: "20px 20px 32px", display: "flex", flexDirection: "column", gap: 24 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// The small capitals over each field, and the quiet bordered button, shared by the editors.
export const fieldLabel = () => ({ fontSize: 13, fontWeight: 700, color: C.textMid, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 });
export const secondaryButton = () => ({ minHeight: 44, padding: "0 16px", borderRadius: C.rsm, border: `1px solid ${C.border}`, background: C.surface2, color: C.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" });

// Delete, then "Yes, delete this …" on the second tap.
export function DeleteButton({ noun, confirming, deleting, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ alignSelf: "flex-start", minHeight: 44, padding: "0 4px", background: "none", border: "none", color: C.red, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
      {deleting ? "Deleting…" : confirming ? `Yes, delete this ${noun}` : `Delete ${noun}`}
    </button>
  );
}
